import { Organization, User } from '@raystack/frontier';
import type { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';

interface Visitor<T> {
  fn: () => Promise<T>;
  stopCondition: (p: Awaited<T>) => boolean;
  timer?: number;
  onStop?: VoidFunction;
}

export function visitor<T>({
  fn,
  stopCondition,
  timer = 1000,
  onStop
}: Visitor<T>) {
  let timeout: null | NodeJS.Timeout = null;

  function dispose() {
    if (timeout) clearTimeout(timeout);
    timeout = null;
    onStop?.();
  }

  function getResult(): Promise<T> {
    return new Promise((resolve, reject) => {
      async function fetcher() {
        try {
          const res = await fn();

          if (stopCondition(res)) {
            dispose();
            return resolve(res);
          }

          timeout = setTimeout(() => fetcher(), timer);
        } catch (err) {
          console.error(err);

          if (err instanceof Error) {
            reject(err);
          } else {
            reject(String(err));
          }
        }
      }

      fetcher();
    });
  }

  return { getResult };
}

function startDownload(href: string, fileName: string) {
  // const popup = window.open(href, '_blank', 'width=1,height=1,left=0,top=0');
  // if (popup) return;

  const a = document.createElement('a');
  a.href = href;
  a.target = '_blank';
  a.download = fileName;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function download(response: AxiosResponse, fileName: string) {
  const { headers, data } = response;

  if (data.signed_url) {
    startDownload(data.signed_url, fileName);
    return;
  }

  const blob = new Blob([data], { type: headers['content-type'] });
  startDownload(window.URL.createObjectURL(blob), fileName);
}

export function setEndDate(date: Date) {
  const endDate = dayjs(date).add(1, 'day').startOf('day');

  return endDate.toISOString();
}

export function reportIssue({
  email,
  subject
}: {
  email: string;
  subject: string;
}) {
  window.open(
    `${import.meta.env.VITE_REPORTISSUE_URL}?tf_anonymous_requester_email=${email}&tf_subject=${subject}`,
    '_blank'
  );
}

export const getUserName = (user: User | Organization) =>
  user?.title || (user as User)?.email || '';

export function getInitials(val: { email?: string; title?: string }): string {
  const { email = '', title } = val || {};
  const initials =
    _.flow(
      str => _.split(str, ' '),
      arr => _.map(arr, _.first),
      arr => _.take(arr, 2),
      arr => _.join(arr, '')
    )(title) ||
    email?.charAt(0) ||
    'NA';
  return initials.toUpperCase();
}

export const stringifyJson = (json = {}) => {
  let str = '';

  try {
    str = JSON.stringify(json);
  } catch (e) {
    console.error('Error stringifying JSON:', e);
  }

  return str;
};
